'use client'

import { NestedFigsThemeProvider } from '@syconium/little-miss-figgy'
import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import {
  TypeStyleHeadlineExtraExtraSmall,
  TypeStyleHeadlineExtraSmall,
  TypeStyleHeadlineSmall,
} from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import {
  GetContentfulPageTileSectionQuery,
  PageTileSectionFragmentFragment,
  PageTileSectionTileFragmentFragment,
  PageTileSectionTileTextAlignment,
  PageTileSectionTileTextPlacement,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics/analytics'
import { floatToTwoDigitHex } from '../../../../../lib/utils/colors'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { NextLink } from '../../../navigation/NextLink'
import {
  LIFTED_BUTTONS_DEFAULT_PIXELS,
  TilesLayout,
  useTilesLayoutContext,
} from '../../../tiles/Tiles.client'
import {
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMd,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../PageSection.client'
import { getContentfulPageSectionAnalyticsName } from '../ContentfulPageSectionProvider.utils'

import {
  Container,
  ImageWrap,
  PageTileSectionTitle,
  StyledButtonRow,
  StyledContentfulStyledLink,
  StyledTileTextContent,
  Tile,
  TileConfiguredOverlay,
  TileConfiguredOverlayContent,
  TileConfiguredTextBelow,
} from './ContentfulPageTileSection.styles'

export const pageTileSectionTileQueryFragment = gql(`
  fragment PageTileSectionTileFragment on PageTileSectionTile {
    __typename
    ctaLink {
      ...StyledLinkFragment
    }
    ctaLinkHref
    ctaText
    imageAspectRatio
    textPlacement
    textAlignment
    description
    hoverImage
    hoverVideo
    id
    image
    inlineVideo
    inlineVimeoId
    jumboTitle
    linkHref
    name
    titleImage
    textAlignment
    textPlacement
    overlayOpacity
    overlayColor
  }
`)

export const pageTileSectionQueryFragment = gql(`
  fragment PageTileSectionFragment on PageTileSection {
    __typename
    id
    name
    numColsDesktop
    numColsMobile
    tileAspectRatio
    textAlign
    textPlacement
    tiles {
      ...PageTileSectionTileFragment
    }
    title
    titleTextSize
    variantDesktop
    variantMobile
    titleAlignmentDesktop
    titleAlignmentMobile
    hasHorizontalMargin
    hasVerticalMargin
    hasTileGaps
  }
`)

export type ContentfulPageTileSectionProps = {
  pageSectionIndex: number
  data: PageTileSectionFragmentFragment
  shouldUseCollectionStyling?: boolean
}

// There are no dedicated types for these CMS values, in contentful-gateway PageTileSection layout options are type String (no enum)
export type SectionPlacement = 'center' | 'bottom' | 'below'
export type SectionAlignment = 'left' | 'center' | 'right'

/**
 * Helper function to map CMS tile placement values to component values
 * and determine the resolved placement considering fallbacks
 */
const resolveTilePlacement = (
  tilePlacement: PageTileSectionTileTextPlacement | null,
  sectionPlacement: SectionPlacement
): PageTileSectionTileTextPlacement => {
  if (tilePlacement) return tilePlacement

  if (sectionPlacement === 'center') {
    return 'overlayCenter'
  } else if (sectionPlacement === 'bottom') {
    return 'overlayBottom'
  } else {
    return 'below'
  }
}

/**
 * Helper function to resolve text alignment with fallback chain
 */

const resolveTileAlignment = (
  tileAlignment: PageTileSectionTileTextAlignment | null,
  sectionAlignment: SectionAlignment
): PageTileSectionTileTextAlignment => {
  return tileAlignment ?? sectionAlignment
}

export const ContentfulPageTileSection = ({
  data,
  pageSectionIndex,
  shouldUseCollectionStyling,
}: ContentfulPageTileSectionProps) => {
  const translations = useTranslation()

  const section = data

  const title = section.title
  const titleTextSize = section.titleTextSize
  const titleAlignmentDesktop = section.titleAlignmentDesktop
  const titleAlignmentMobile = section.titleAlignmentMobile

  const hasHorizontalMargin = section.hasHorizontalMargin

  const layoutVariantDesktop =
    section.variantDesktop === 'carousel' ? 'slider' : section.variantDesktop
  const layoutVariantMobile =
    section.variantMobile === 'carousel' ? 'slider' : section.variantMobile
  const layout = { sm: layoutVariantMobile, md: layoutVariantDesktop }
  const tileGaps = section.hasTileGaps
    ? undefined
    : {
        row: {
          sm: 0,
          md: 0,
        },
        column: {
          sm: 0,
          md: 0,
        },
      }

  const numColsDesktop = section.numColsDesktop
  const numColsMobile = section.numColsMobile

  const tiles = section.tiles

  const textPlacement = section.textPlacement

  return (
    <PageSection hasVerticalMargin={!shouldUseCollectionStyling && section.hasVerticalMargin}>
      <PageSectionContent
        gutterSize={
          hasHorizontalMargin
            ? layoutVariantDesktop === 'slider'
              ? 'expanded'
              : 'default'
            : 'none'
        }
      >
        {title && (
          <>
            <PageTileSectionTitle
              $titleTextSize={titleTextSize}
              $textAlign={titleAlignmentDesktop ?? undefined}
              $mobileTextAlign={titleAlignmentMobile ?? undefined}
            >
              {title}
            </PageTileSectionTitle>
          </>
        )}
      </PageSectionContent>

      <PageSectionContent gutterSize='none'>
        <TilesLayout
          visibleTiles={{ sm: numColsMobile, md: numColsDesktop }}
          layout={layout}
          scrollBackLabel={translations.magnolia.general.scrollBack}
          scrollForwardLabel={translations.magnolia.general.scrollForward}
          liftButtonsPixels={textPlacement === 'below' ? LIFTED_BUTTONS_DEFAULT_PIXELS : 0}
          gutters={{
            sm: !hasHorizontalMargin ? 0 : sectionSideGutterUntilMd,
            md: !hasHorizontalMargin
              ? 0
              : layoutVariantDesktop === 'slider'
              ? sectionSideGutterFromMdAndExpanded
              : sectionSideGutterFromMd,
          }}
          justifyContent={{
            sm: layoutVariantMobile === 'grid' ? 'center' : 'left',
            md: layoutVariantDesktop === 'grid' ? 'center' : 'left',
          }}
          gaps={tileGaps}
        >
          {tiles.map((tile, index) => {
            return (
              <PageTileSectionTile
                key={index}
                section={section}
                pageSectionIndex={pageSectionIndex}
                tile={tile}
                tileIndex={index}
                shouldUseCollectionStyling={shouldUseCollectionStyling}
              />
            )
          })}
        </TilesLayout>
      </PageSectionContent>
    </PageSection>
  )
}

type ContentfulPageTileSection = Extract<
  NonNullable<GetContentfulPageTileSectionQuery['pageViewSection']>,
  { __typename: 'PageTileSection' }
>

const PageTileSectionTile: React.FC<{
  tile: PageTileSectionTileFragmentFragment
  section: PageTileSectionFragmentFragment
  pageSectionIndex: number
  tileIndex: number
  shouldUseCollectionStyling?: boolean
}> = ({ tile, section, pageSectionIndex, tileIndex, shouldUseCollectionStyling }) => {
  const translations = useTranslation()
  const tilesLayoutContext = useTilesLayoutContext()!
  const category = getContentfulPageSectionAnalyticsName(section.__typename)
  const pageSectionDisplayName = section.title ?? undefined
  const pageSectionContentfulName = section.name ?? undefined
  const hasVerticalMargin = section.hasVerticalMargin
  const sectionTilePlacement = section.textPlacement
  const sectionTileAlignment = section.textAlign
  const { textPlacement, textAlignment } = tile
  const tileAspectRatioOverride =
    section.tileAspectRatio && section.tileAspectRatio > 0 ? section.tileAspectRatio : undefined
  const analytics = {
    category,
    pageSectionContentfulName,
    pageSectionDisplayName,
    pageSectionIndex,
  }

  const isTwoUp =
    section.numColsDesktop === 2 && section.numColsMobile === 1 && section.tiles.length === 2
  const hasHover = !!(tile.hoverImage || tile.hoverVideo)
  const gridAspectRatio = 0.9014
  const sliderAspectRatio = 0.7435 // design: 204 x 273
  const twoUpAspectRatio = 1.217 // design: 730x600

  const getAspectRatio = (layout: string) => {
    if (isTwoUp) return twoUpAspectRatio
    if (layout === 'slider') return sliderAspectRatio
    return gridAspectRatio
  }

  const aspectRatios = tileAspectRatioOverride
    ? {
        sm: tileAspectRatioOverride,
        md: tileAspectRatioOverride,
      }
    : !isTwoUp && shouldUseCollectionStyling
    ? {
        sm: 1.3333,
        md: 1.3333,
      }
    : {
        sm: getAspectRatio(tilesLayoutContext.layout.sm),
        md: getAspectRatio(tilesLayoutContext.layout.md),
      }

  const roundCorners = {
    sm: tilesLayoutContext.layout.sm === 'slider' && !shouldUseCollectionStyling,
    md: tilesLayoutContext.layout.md === 'slider' && !shouldUseCollectionStyling,
  }

  const resolvedPlacement = resolveTilePlacement(textPlacement, sectionTilePlacement)
  const resolvedAlignment = resolveTileAlignment(textAlignment, sectionTileAlignment)
  const isTileOverlay = resolvedPlacement !== 'below'
  const imageContentTheme = tile.overlayColor === 'dark' ? 'dark' : 'light'
  const isButtonLink = tile.ctaLink?.linkStyle === 'button'

  const tileTextContent = (
    <StyledTileTextContent $isOverlay={isTileOverlay}>
      {tile.jumboTitle && (
        <TypeStyleHeadlineSmall isUppercase={isUppercase(tile.jumboTitle)}>
          {tile.jumboTitle}
        </TypeStyleHeadlineSmall>
      )}
      {tile.name && (
        <TypeStyleHeadlineExtraSmall isUppercase={isUppercase(tile.name)}>
          {tile.name}
        </TypeStyleHeadlineExtraSmall>
      )}
      {tile.description && (
        <TypeStyleHeadlineExtraExtraSmall isUppercase={isUppercase(tile.description)}>
          {tile.description}
        </TypeStyleHeadlineExtraExtraSmall>
      )}
    </StyledTileTextContent>
  )

  return (
    <Tile>
      <Container
        $hasHover={hasHover}
        $textAlign={resolvedAlignment}
        $hasVerticalMargin={hasVerticalMargin}
        $hasOverlay={isTileOverlay}
        $shouldUseCollectionStyling={shouldUseCollectionStyling}
        key={tileIndex}
      >
        <ImageWrap
          $aspectRatios={aspectRatios}
          $roundCorners={roundCorners}
          $shouldUseCollectionStyling={shouldUseCollectionStyling}
          $hasOverlay={isTileOverlay}
          $isTwoUp={isTwoUp}
        >
          {tile.hoverImage && (
            <ResponsiveImage
              alt=''
              loading='lazy'
              src={tile.hoverImage}
              widths={{
                unit: 'vw',
                sm: 100 / tilesLayoutContext.visibleTiles.sm,
                md: 100 / tilesLayoutContext.visibleTiles.md,
              }}
              aspectRatios={null}
            />
          )}
          {tile.hoverVideo && <video src={tile.hoverVideo} autoPlay muted playsInline loop />}
          {tile.image && (
            <ResponsiveImage
              alt=''
              loading='lazy'
              src={tile.image}
              widths={{
                unit: isTwoUp ? 'px' : 'vw',
                sm: isTwoUp ? 414 : 100 / tilesLayoutContext.visibleTiles.sm,
                md: isTwoUp ? 730 : 100 / tilesLayoutContext.visibleTiles.md,
              }}
              aspectRatios={
                isTwoUp
                  ? { sm: 1.1436, md: twoUpAspectRatio }
                  : shouldUseCollectionStyling
                  ? { sm: 1.3333, md: 1.3333 }
                  : null
              }
            />
          )}
          <ClientOnlyResponsiveVideo
            playText={translations.magnolia.general.playVideo}
            pauseText={translations.magnolia.general.playVideo}
            inlineVideoDesktop={tile.inlineVideo}
            inlineVideoMobile={tile.inlineVideo}
            inlineVimeoIdDesktop={tile.inlineVimeoId}
            inlineVimeoIdMobile={tile.inlineVimeoId}
          />
        </ImageWrap>

        {/** New overlay absolutely positioned, but rendered outside of ImageWrap */}
        {/** Only overlay content should be rendered here */}
        {isTileOverlay && (
          <NestedFigsThemeProvider themeDesktop={imageContentTheme} themeMobile={imageContentTheme}>
            <TileConfiguredOverlay
              $placement={resolvedPlacement}
              $textAlignment={resolvedAlignment}
              $hexOverlayOpacity={
                tile.overlayOpacity ? floatToTwoDigitHex(tile.overlayOpacity) : null
              }
              as={!tile.ctaLink ? NextLink : 'div'}
              href={!tile.ctaLink ? tile.linkHref : undefined}
              {...trackEvent({
                customAttributes: {
                  'data-heap-description': tile.description ?? undefined,
                },
                category: analytics.category,
                action: 'click image',
                pageSectionName: analytics.pageSectionDisplayName,
                pageSectionIndex: analytics.pageSectionIndex,
                contentfulName: analytics.pageSectionContentfulName,
              })}
            >
              <TileConfiguredOverlayContent $alignment={resolvedAlignment}>
                {tile.titleImage && (
                  <ResponsiveImage
                    src={tile.titleImage}
                    loading='lazy'
                    alt={''}
                    aspectRatios={{ sm: 1, md: 1 }}
                    widths={{
                      unit: 'vw',
                      sm: 100 / tilesLayoutContext.visibleTiles.sm,
                      md: 100 / tilesLayoutContext.visibleTiles.md,
                    }}
                  />
                )}
                {tileTextContent}
                <StyledButtonRow $textAlignment={resolvedAlignment}>
                  {tile.ctaLink && (
                    <StyledContentfulStyledLink {...tile.ctaLink} $isButtonLink={isButtonLink} />
                  )}
                </StyledButtonRow>
              </TileConfiguredOverlayContent>
            </TileConfiguredOverlay>
          </NestedFigsThemeProvider>
        )}

        {!isTileOverlay && (
          <TileConfiguredTextBelow $textAlignment={resolvedAlignment} $isTwoUp={isTwoUp}>
            {tileTextContent}
            <StyledButtonRow $textAlignment={resolvedAlignment}>
              {tile.ctaLink && (
                <StyledContentfulStyledLink {...tile.ctaLink} $isButtonLink={isButtonLink} />
              )}
            </StyledButtonRow>
          </TileConfiguredTextBelow>
        )}
      </Container>
    </Tile>
  )
}
